import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const W = 320, H = 400, PADDLE_W = 64, PADDLE_H = 12, BALL = 6;
const COLS = 7, ROWS = 5, BRICK_H = 18, GAP = 4, TOP = 40;
const BW = (W - GAP) / COLS - GAP;
const COLORS = ["#C1121F", "#E76F51", "#E8B04B", "#2D6A4F", "#457B9D"];

export default function BrickBreaker() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const move = useRef(0);
  const livesRef = useRef(3);
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [status, setStatus] = useState("idle");

  const newBricks = () => {
    const b = [];
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) b.push({ x: GAP + c * (BW + GAP), y: TOP + r * (BRICK_H + GAP), alive: true, color: COLORS[r] });
    return b;
  };
  const resetBall = () => { const s = g.current; s.bx = W / 2; s.by = H - 60; s.vx = 2.6 * (Math.random() > 0.5 ? 1 : -1); s.vy = -3.4; s.px = W / 2 - PADDLE_W / 2; };
  const start = () => { g.current = { bricks: newBricks() }; resetBall(); scoreRef.current = 0; setScore(0); livesRef.current = 3; setLives(3); setStatus("playing"); sfx.beep(); };

  useEffect(() => {
    const kd = (e) => { if (["ArrowLeft", "a"].includes(e.key)) { move.current = -1; e.preventDefault(); } else if (["ArrowRight", "d"].includes(e.key)) { move.current = 1; e.preventDefault(); } };
    const ku = (e) => { if (["ArrowLeft", "a", "ArrowRight", "d"].includes(e.key)) move.current = 0; };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  useEffect(() => {
    const cvs = canvasRef.current, ctx = cvs.getContext("2d");
    const drag = (e) => { if (status !== "playing") return; const r = cvs.getBoundingClientRect(); const x = ((e.clientX) - r.left) / r.width * W; g.current.px = Math.max(0, Math.min(W - PADDLE_W, x - PADDLE_W / 2)); };
    cvs.addEventListener("pointermove", drag);
    const draw = () => {
      ctx.fillStyle = "#12121a"; ctx.fillRect(0, 0, W, H);
      const s = g.current;
      (s.bricks || []).forEach((b) => { if (b.alive) { ctx.fillStyle = b.color; ctx.fillRect(b.x, b.y, BW, BRICK_H); } });
      ctx.fillStyle = "#e8b04b"; ctx.fillRect(s.px || 0, H - 24, PADDLE_W, PADDLE_H);
      ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(s.bx || W / 2, s.by || H / 2, BALL, 0, 7); ctx.fill();
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      s.px = Math.max(0, Math.min(W - PADDLE_W, s.px + move.current * 6));
      s.bx += s.vx; s.by += s.vy;
      if (s.bx < BALL) { s.vx = Math.abs(s.vx); s.bx = BALL; sfx.click(); }
      if (s.bx > W - BALL) { s.vx = -Math.abs(s.vx); s.bx = W - BALL; sfx.click(); }
      if (s.by < BALL) { s.vy = Math.abs(s.vy); s.by = BALL; }
      if (s.by > H - 24 - BALL && s.by < H - 24 + PADDLE_H && s.bx > s.px && s.bx < s.px + PADDLE_W && s.vy > 0) {
        s.vy = -Math.abs(s.vy); s.vx = ((s.bx - (s.px + PADDLE_W / 2)) / (PADDLE_W / 2)) * 4; s.by = H - 24 - BALL; sfx.click();
      }
      for (const b of s.bricks) {
        if (b.alive && s.bx > b.x && s.bx < b.x + BW && s.by > b.y && s.by < b.y + BRICK_H) { b.alive = false; s.vy *= -1; scoreRef.current += 10; setScore(scoreRef.current); sfx.hit(); break; }
      }
      if (!s.bricks.some((b) => b.alive)) { cancelAnimationFrame(raf); sfx.win(); setStatus("win"); return; }
      if (s.by > H) {
        livesRef.current -= 1; setLives(livesRef.current);
        if (livesRef.current <= 0) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); return; }
        sfx.lose(); resetBall();
      }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); cvs.removeEventListener("pointermove", drag); };
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2 flex gap-2"><AScore label="Score" value={score} /><AScore label="Lives" value={"❤".repeat(lives) || "0"} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[300px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-3">
            {status === "over" && <AResult win={false} text={`Game Over · ${score}`} testId="brick-over" />}
            {status === "win" && <AResult win={true} text={`Cleared! · ${score}`} testId="brick-win" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 text-center text-xs text-kirana-brown/60">Drag on the board or use the buttons</p>
      <div className="mt-1 flex gap-4">
        <PadButton testId="brick-left" onPress={() => { move.current = -1; }} onRelease={() => { move.current = 0; }}><ArrowLeft className="h-5 w-5" /></PadButton>
        <PadButton testId="brick-right" onPress={() => { move.current = 1; }} onRelease={() => { move.current = 0; }}><ArrowRight className="h-5 w-5" /></PadButton>
      </div>
    </div>
  );
}
